// Speak instead of typing: the browser's own speech recognition (Chrome, Edge, Safari). Nothing is
// recorded or sent by Market Matrix; the browser turns speech into text. Browsers without it
// (Firefox) don't show the microphone.
import { useEffect, useRef, useState } from 'react';
import { useI18n } from './i18n';
import { currentLocale } from './langState';

const Recognition = typeof window !== 'undefined' ? window.SpeechRecognition || window.webkitSpeechRecognition : null;
export const speechSupported = Boolean(Recognition);

// onText(text) gets each finished phrase. Returns { listening, start, stop, error }.
export function useSpeech(onText) {
  const [listening, setListening] = useState(false);
  const [error, setError] = useState('');
  const rec = useRef(null);
  const handler = useRef(onText);
  handler.current = onText;

  useEffect(() => () => rec.current?.abort(), []);

  function start() {
    if (!Recognition || listening) return;
    setError('');
    const r = new Recognition();
    r.lang = currentLocale().startsWith('es') ? 'es-MX' : 'en-US';
    r.continuous = true;
    r.interimResults = false;
    r.onresult = (e) => {
      for (let i = e.resultIndex; i < e.results.length; i += 1) {
        if (e.results[i].isFinal) {
          const text = e.results[i][0].transcript.trim();
          if (text) handler.current(text);
        }
      }
    };
    r.onerror = (e) => {
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') setError('mic-blocked');
      else if (e.error !== 'no-speech' && e.error !== 'aborted') setError('mic-failed');
    };
    r.onend = () => setListening(false);
    rec.current = r;
    try {
      r.start();
      setListening(true);
    } catch {
      setListening(false);
    }
  }

  function stop() {
    rec.current?.stop();
    setListening(false);
  }

  return { listening, start, stop, error };
}

// A microphone button that adds what's said to a text: onText(phrase).
export function MicButton({ onText, className = '' }) {
  const { t } = useI18n();
  const { listening, start, stop, error } = useSpeech(onText);
  if (!speechSupported) return null;
  const label = listening ? t('Stop dictation') : t('Speak instead of typing');
  return (
    <span className="mic-wrap">
      <button type="button" className={`mic-button${listening ? ' listening' : ''} ${className}`} onMouseDown={(e) => e.preventDefault()}
        onClick={listening ? stop : start} aria-pressed={listening} aria-label={label} title={label}>
        <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21M8.5 21h7" />
        </svg>
        {listening && <span className="mic-live">{t('Listening…')}</span>}
      </button>
      {error && (
        <span className="mic-error" role="alert">
          {error === 'mic-blocked' ? t('Allow the microphone for this site in your browser to speak your notes.') : t('Dictation stopped. Try again.')}
        </span>
      )}
    </span>
  );
}

// Adds a spoken phrase to the end of a text, with a space if needed.
export const appendPhrase = (text, phrase) => (text && !/\s$/.test(text) ? `${text} ${phrase}` : `${text || ''}${phrase}`);
